import { useEffect, useState } from "react"
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import { CampusClient } from "@clients/campusClient"
import { formatProgramName } from "./programLabel"

type CourseContentProgress = {
  courseId: string | null
  courseTitle: string
  termSequenceNumber: number | null
  percentComplete: number
  isOpen: boolean
  completed: boolean
}

type ContentStatus =
  | "excellent"
  | "on-track"
  | "needs-attention"
  | "at-risk"
  | "no-data"

// One section per active program (campus orders running programs first).
type ProgramContentSnapshot = {
  courses: CourseContentProgress[]
  lastLearningActivity: string | null
  overallCompletion: number | null
  status: ContentStatus
  offeringClientId: string
  programId: string | null
  programTitle: string
  domain: string | null
  cohortStartDate: string | null
  // Started and not ended. A not-running program legitimately reads 0% /
  // no-data, so it renders last and de-emphasized and never sets the tone.
  isRunning: boolean
}

const STATUS_PRESENTATION: Record<
  ContentStatus,
  { label: string; color: "success" | "warning" | "error" | "default" }
> = {
  excellent: { label: "Excellent", color: "success" },
  "on-track": { label: "On track", color: "success" },
  "needs-attention": { label: "Needs attention", color: "warning" },
  "at-risk": { label: "At risk", color: "error" },
  "no-data": { label: "No open courses yet", color: "default" },
}

const barColor = (percent: number): "success" | "warning" | "error" => {
  if (percent >= 0.8) return "success"
  if (percent >= 0.6) return "warning"
  return "error"
}

// Same fade-at-the-cut scroll affordance as the programs tile (cover color =
// the panel background), so the two scrolling tile bodies read as one system.
const tileScrollSx = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "3px",
  },
  background: `
    linear-gradient(#fafaf7 30%, rgba(250, 250, 247, 0)) center top,
    linear-gradient(rgba(250, 250, 247, 0), #fafaf7 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center top,
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center bottom`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 32px, 100% 32px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
} as const

const daysSince = (iso: string | null): number | null => {
  if (!iso) return null
  const then = new Date(iso)
  if (isNaN(then.getTime())) return null
  return Math.max(
    0,
    Math.round((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24)),
  )
}

const lastProgressText = (iso: string | null): string | null => {
  const days = daysSince(iso)
  if (days === null) return null
  return days === 0 ? "last progress today" : `last progress ${days}d ago`
}

// Lead with the program's real name (its campus title, e.g. "IHK Data
// Analytics") + cohort — the same label the add-program picker shows — never
// the school/domain, which repeated across every Data Analysis program.
const programName = (snapshot: ProgramContentSnapshot): string =>
  snapshot.cohortStartDate
    ? formatProgramName({
        name: snapshot.programTitle,
        domain: snapshot.domain ?? "",
        startDate: snapshot.cohortStartDate,
      })
    : snapshot.programTitle

function CourseBar(props: { course: CourseContentProgress }) {
  const { course } = props
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <Tooltip title={course.courseTitle}>
          {course.courseId ? (
            // The row's shortcut to its course page — same tab, quiet ink
            // with a hover underline so the bars stay a chart, not a link
            // wall. Rows without a course id (title-only units) stay plain.
            <Link
              component={RouterLink}
              to={`/course-manager/course/${course.courseId}`}
              onClick={(event) => event.stopPropagation()}
              variant="body2"
              noWrap
              sx={{
                minWidth: 0,
                color: "text.primary",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {course.courseTitle}
            </Link>
          ) : (
            <Typography variant="body2" noWrap>
              {course.courseTitle}
            </Typography>
          )}
        </Tooltip>
        <Typography
          variant="caption"
          color={course.isOpen ? "text.secondary" : "text.disabled"}
          sx={{ flexShrink: 0 }}
        >
          {course.isOpen
            ? `${Math.round(course.percentComplete * 100)}%`
            : "not open"}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, course.percentComplete * 100)}
        color={barColor(course.percentComplete)}
        sx={{ height: 6, borderRadius: 3, opacity: course.isOpen ? 1 : 0.4 }}
      />
    </Box>
  )
}

// One program's section: its own "Stuck on … / On track" headline against ITS
// open courses only, plus its bars. Never blends two programs' progress.
function ProgramSection(props: { snapshot: ProgramContentSnapshot }) {
  const { snapshot } = props

  const openCoursesWorstFirst = snapshot.courses
    .filter((course) => course.isOpen)
    .sort((a, b) => a.percentComplete - b.percentComplete)
  const notOpenCourses = snapshot.courses.filter((course) => !course.isOpen)
  const orderedCourses = [...openCoursesWorstFirst, ...notOpenCourses]

  const completedCount = openCoursesWorstFirst.filter(
    (course) => course.completed,
  ).length
  const openCoursesCount = openCoursesWorstFirst.length

  const needsAttention =
    snapshot.status === "needs-attention" || snapshot.status === "at-risk"
  const stuckCourse = openCoursesWorstFirst[0]
  const progressText = lastProgressText(snapshot.lastLearningActivity)
  let headline: string | null = null
  if (snapshot.status !== "no-data") {
    if (needsAttention && stuckCourse) {
      headline = [
        `Stuck on: ${stuckCourse.courseTitle} · ${Math.round(
          stuckCourse.percentComplete * 100,
        )}%`,
        progressText,
      ]
        .filter(Boolean)
        .join(" · ")
    } else {
      headline = [
        `On track · ${completedCount}/${openCoursesCount} courses complete`,
        progressText,
      ]
        .filter(Boolean)
        .join(" · ")
    }
  }

  return (
    // Not-running programs are dimmed: they legitimately read 0% / no-data and
    // must not compete with the student's real, running program for attention.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        opacity: snapshot.isRunning ? 1 : 0.55,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Tooltip title={programName(snapshot)}>
          <Typography variant="body2_sb" noWrap sx={{ minWidth: 0 }}>
            {programName(snapshot)}
          </Typography>
        </Tooltip>
        <Chip
          size="small"
          color={STATUS_PRESENTATION[snapshot.status].color}
          label={STATUS_PRESENTATION[snapshot.status].label}
          variant={snapshot.status === "no-data" ? "outlined" : "filled"}
        />
      </Box>
      {headline && (
        <Tooltip title={headline}>
          <Typography
            variant="body2"
            noWrap
            color={needsAttention ? "warning.dark" : "text.primary"}
          >
            {headline}
          </Typography>
        </Tooltip>
      )}
      {orderedCourses.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {orderedCourses.map((course) => (
            <CourseBar
              key={`${course.courseId}-${course.courseTitle}`}
              course={course}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

// The content counterpart of the attendance quick-look: what needs eyes, per
// program (same math the campus ASM dashboard uses, served by campus). A
// multi-program student sees every program, running first.
export function StudentContentSnapshot(props: { studentId: string }) {
  const { studentId } = props
  const [snapshots, setSnapshots] = useState<ProgramContentSnapshot[] | null>(
    null,
  )
  const [error, setError] = useState(false)

  useEffect(() => {
    setSnapshots(null)
    setError(false)
    CampusClient.get<ProgramContentSnapshot[]>(
      `progress/track/student/${studentId}/content-snapshot`,
    )
      .then(setSnapshots)
      .catch(() => setError(true))
  }, [studentId])

  if (error) {
    // Progress is a nice-to-have panel; failures stay quiet and small.
    return null
  }

  return (
    <Box
      sx={{
        px: "16px",
        py: "12px",
        borderBottom: "1px solid #eee",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Stretch-or-center: full-width bars read fine up to ~720px; past
          that they stretch absurdly, so the whole block caps there and
          centers in the tile instead of hugging the left edge. */}
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          width: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: "8px", flexShrink: 0 }}
        >
          Content progress
        </Typography>
        {!snapshots && (
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={18} />
          </Box>
        )}
        {snapshots && snapshots.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            No program content yet
          </Typography>
        )}
        {snapshots && snapshots.length > 0 && (
          // ONE scroll container for every program section — the "Content
          // progress" label above stays pinned; the sections scroll together.
          <Box
            data-content-tile-body
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              ...tileScrollSx,
            }}
          >
            {snapshots.map((snapshot) => (
              <ProgramSection
                key={snapshot.offeringClientId}
                snapshot={snapshot}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
